// Base API configuration
const LOCAL_API_BASE_URL = 'http://localhost:5000/api';

const normalizeApiBaseUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  const trimmed = url.trim().replace(/\/+$/, '');
  if (!trimmed) return null;

  if (trimmed.endsWith('/api')) return trimmed;
  return `${trimmed}/api`;
};

const buildApiBaseCandidates = () => {
  const candidates = [];
  const isBrowser = typeof window !== 'undefined';
  const browserHost = isBrowser ? window.location.hostname : '';
  const isLocalHost = ['localhost', '127.0.0.1', '::1'].includes(browserHost);

  // Explicit REACT_APP_API_URL always takes highest priority.
  if (process.env.REACT_APP_API_URL) {
    candidates.push(normalizeApiBaseUrl(process.env.REACT_APP_API_URL));
  }

  // In production, use relative "/api" path first (works with same-domain deployment)
  if (!process.env.REACT_APP_API_URL && isBrowser && !isLocalHost) {
    candidates.push('/api');
  }

  // If no explicit API URL is configured, use same-origin in production.
  if (!process.env.REACT_APP_API_URL && isBrowser && !isLocalHost && candidates.length === 0) {
    candidates.push(normalizeApiBaseUrl(window.location.origin));
  }

  // Keep localhost fallback available for local development.
  if (isLocalHost || candidates.filter(Boolean).length === 0) {
    candidates.push(LOCAL_API_BASE_URL);
  }

  return [...new Set(candidates.filter(Boolean))];
};

const API_BASE_CANDIDATES = buildApiBaseCandidates();
const API_BASE_URL = API_BASE_CANDIDATES[0] || LOCAL_API_BASE_URL;

const isNetworkFetchError = (error) => {
  return (
    error instanceof TypeError &&
    /Failed to fetch|NetworkError|Load failed/i.test(error.message || '')
  );
};

const isHtmlPayload = (text) => {
  if (!text || typeof text !== 'string') return false;
  const normalized = text.trim();
  return normalized.startsWith('<') || normalized.includes('DOCTYPE html');
};

const shouldTryNextBase = (error, hasMoreCandidates) => {
  if (!hasMoreCandidates) return false;
  if (isNetworkFetchError(error)) return true;

  if (error?.name === 'ApiParseError' && error.isHtmlResponse) {
    return true;
  }

  if ([404, 502, 503, 504].includes(error?.status)) {
    return true;
  }

  return false;
};

const parseResponseData = async (response, url) => {
  let text = '';

  try {
    text = await response.text();
    if (text) {
      text = text.trim().replace(/^\uFEFF/, '');
    }

    return text ? JSON.parse(text) : {};
  } catch (parseError) {
    console.error(`API Parse Error for URL: ${url}`);
    console.error('Parse Error:', parseError.message);
    console.error('Response status:', response.status);
    console.error('Response body:', text);
    console.error('Response body (debug):', JSON.stringify(text));

    const htmlResponse = isHtmlPayload(text);
    if (htmlResponse) {
      console.error('Received HTML instead of JSON. The API base URL may be incorrect for this environment.');
    }

    const error = new Error('Invalid response from server (check console for details)');
    error.name = 'ApiParseError';
    error.status = response.status;
    error.isHtmlResponse = htmlResponse;

    if (!response.ok) {
      error.message = `Server error: ${response.status} ${response.statusText}`;
    }

    throw error;
  }
};

const executeRequestWithFallback = async (endpoint, options = {}) => {
  const token = getAuthToken();

  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    credentials: 'include',
    ...options,
  };

  const attemptedUrls = [];
  let lastError = null;

  for (let i = 0; i < API_BASE_CANDIDATES.length; i += 1) {
    const baseUrl = API_BASE_CANDIDATES[i];
    const hasMoreCandidates = i < API_BASE_CANDIDATES.length - 1;
    const url = `${baseUrl}${endpoint}`;
    attemptedUrls.push(url);

    try {
      const response = await fetch(url, config);
      const data = await parseResponseData(response, url);

      if (!response.ok) {
        const error = new Error(data.message || `Server error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      return data;
    } catch (error) {
      lastError = error;

      if (shouldTryNextBase(error, hasMoreCandidates)) {
        console.warn(`API request failed via ${baseUrl}. Retrying with fallback base URL...`);
        continue;
      }

      break;
    }
  }

  if (isNetworkFetchError(lastError) || (lastError?.name === 'ApiParseError' && lastError?.isHtmlResponse)) {
    const friendlyError = new Error(
      `Cannot connect to the backend API. Attempted: ${attemptedUrls.join(' | ')}`
    );
    friendlyError.name = 'ConnectionError';
    console.error('API Request Error: Backend server is not running or not accessible');
    console.error('Attempted URLs:', attemptedUrls);
    throw friendlyError;
  }

  throw lastError;
};

const executeFormRequestWithFallback = async (endpoint, formData, options = {}) => {
  const token = getAuthToken();

  const config = {
    method: 'POST',
    headers: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
    body: formData,
    credentials: 'include',
    ...options,
  };

  const attemptedUrls = [];
  let lastError = null;

  for (let i = 0; i < API_BASE_CANDIDATES.length; i += 1) {
    const baseUrl = API_BASE_CANDIDATES[i];
    const hasMoreCandidates = i < API_BASE_CANDIDATES.length - 1;
    const url = `${baseUrl}${endpoint}`;
    attemptedUrls.push(url);

    try {
      const response = await fetch(url, config);
      const data = await parseResponseData(response, url);

      if (!response.ok) {
        const error = new Error(data.message || `Server error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      return data;
    } catch (error) {
      lastError = error;

      if (shouldTryNextBase(error, hasMoreCandidates)) {
        console.warn(`API form request failed via ${baseUrl}. Retrying with fallback base URL...`);
        continue;
      }

      break;
    }
  }

  if (isNetworkFetchError(lastError) || (lastError?.name === 'ApiParseError' && lastError?.isHtmlResponse)) {
    const friendlyError = new Error(
      `Cannot connect to the backend API. Attempted: ${attemptedUrls.join(' | ')}`
    );
    friendlyError.name = 'ConnectionError';
    console.error('API Form Request Error: Backend server is not running or not accessible');
    console.error('Attempted URLs:', attemptedUrls);
    throw friendlyError;
  }

  throw lastError;
};

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem('token') || '';
};

// Helper function to make API requests
const apiRequest = async (endpoint, options = {}) => {
  try {
    return await executeRequestWithFallback(endpoint, options);
  } catch (error) {
    if (error.status !== 401 && error.name !== 'ConnectionError') {
      console.error('API Request Error:', error);
    }
    throw error;
  }
};

// Helper function for form data requests (file uploads)
const apiFormRequest = async (endpoint, formData, options = {}) => {
  try {
    return await executeFormRequestWithFallback(endpoint, formData, options);
  } catch (error) {
    if (error.status !== 401 && error.name !== 'ConnectionError') {
      console.error('API Form Request Error:', error);
    }
    throw error;
  }
};

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_BASE_URL.replace('/api', '')}/${path.replace(/\\/g, '/')}`;
};

export { apiRequest, apiFormRequest, API_BASE_URL, getImageUrl };
